import api from "../../services/api";
import React, { useEffect, useState } from "react";
import Modal from "react-modal"; // Install this package if not already installed
import "./billing.css";

Modal.setAppElement("#root"); // Replace '#root' with your app's root element ID

const Billing = () => {
  const [payments, setPayments] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [cancellationDate, setCancellationDate] = useState(null);

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        const response = await api.get("/billing/", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        });

        if (response.status !== 200) {
          throw new Error(response.data.error || "Failed to fetch payments.");
        }

        setPayments(response.data.data);
      } catch (err) {
        setError(
          err.response?.data?.error ||
            err.message ||
            "An unexpected error occurred."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, []);

  const openModal = (payment) => {
    setSelectedPayment(payment);
    // Calculate cancellation date as two months from today
    const effectiveDate = new Date();
    effectiveDate.setMonth(effectiveDate.getMonth() + 2);
    setCancellationDate(
      effectiveDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    );
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPayment(null);
    setCancellationDate(null);
  };

  const handleCancelSubscription = async () => {
    if (!selectedPayment) return;

    try {
      const response = await api.post(
        "/cancel-subscription/",
        {
          subscription_id: selectedPayment.id,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
        }
      );

      if (response.status === 200) {
        // Optionally, update the UI to reflect cancellation
        setPayments(
          payments.map((payment) =>
            payment.id === selectedPayment.id
              ? { ...payment, status: "canceled" }
              : payment
          )
        );
        closeModal();
      } else {
        throw new Error(
          response.data.error || "Failed to cancel subscription."
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.message ||
          "An unexpected error occurred."
      );
      closeModal();
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return <div className="error-container">Error: {error}</div>;
  }

  return (
    <div className="billing-container">
      <h2 className="billing-title">Your Past Payments</h2>

      {payments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📊</div>
          <p className="empty-state-text">No payments found.</p>
        </div>
      ) : (
        <table className="billing-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Product</th>
              <th>Subscription Duration</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((payment) => (
              <tr key={payment.id}>
                <td>{new Date(payment.created * 1000).toLocaleDateString()}</td>
                <td>
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: payment.currency.toUpperCase(),
                  }).format(payment.amount / 100)}
                </td>
                <td>
                  <span className={`status-badge status-${payment.status}`}>
                    {capitalizeFirstLetter(payment.status)}
                  </span>
                </td>
                <td>{payment.product_ids || "N/A"}</td>
                <td>
                  {capitalizeFirstLetter(payment.subscription_duration) ||
                    "N/A"}
                </td>
                <td>
                  {payment.status !== "canceled" &&
                  payment.status !== "inactive" ? (
                    <button
                      onClick={() => openModal(payment)}
                      className="action-button cancel-button"
                    >
                      Cancel
                    </button>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        contentLabel="Cancel Subscription"
        className="modal-content"
        overlayClassName="modal-overlay"
      >
        <h2 className="modal-title">Cancel Subscription</h2>
        <p className="modal-text">
          Please note that based on our <strong>Terms of Services</strong>, the
          cancellation request should be submitted at least{" "}
          <strong>40 days</strong> before.
        </p>
        <p className="modal-text">
          Your effective date of cancellation will be{" "}
          <strong>{cancellationDate}</strong>.
        </p>
        <div className="modal-actions">
          <button onClick={closeModal} className="modal-button modal-close">
            Close
          </button>
          <button
            onClick={handleCancelSubscription}
            className="modal-button modal-confirm"
          >
            Cancel Subscription
          </button>
        </div>
      </Modal>
    </div>
  );
};

// Helper function to capitalize first letter
const capitalizeFirstLetter = (string) => {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export default Billing;
